import React, { useEffect, useLayoutEffect, useState } from 'react';
import Navigation from './navigation';
import Footer from './footer';
import AndroidBanner from './androidBanner';
import CompatibilityPopup from '../components/compatibilityPopup';
import { unlocalizedPageCheck } from '../utils/unlocalizedPageCheck';
import { NavToggleContext } from '../components/navToggleContext';
import { getShowPromoBanner, PromoBanner } from '../components/promo';
import LiteBanner from '../components/LiteBanner';

const LocaleContext = React.createContext<any>(undefined);

// Use the built-in Context API to make the "locale" available to every component in the tree
// This e.g. enables the LocalizedLink to function correctly
// As this component wraps every page (due to the wrapPageElement API) we can be sure to have
// the locale available everywhere!
const Layout = ({
    children,
    pageContext: { locale, unlocalizedPath, pageName, navText, meta, footerText, compatibilityText, androidBannerText },
}) => {
    useLayoutEffect(() => {
        if (!window.location.hash) {
            const scrollTimeout = setTimeout(() => {
                // Gatsby focuses the page wrapper after navigation. Reset its
                // scroll offset instantly, including locale-only navigation.
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            }, 0);
            return () => clearTimeout(scrollTimeout);
        }
    }, [unlocalizedPath, locale]);
    // SEO <head> output moved to the Gatsby Head API (src/components/seo.tsx,
    // exported as `Head` per page). isHomepage stays because the banner logic
    // below still uses it.
    const isHomepage = pageName === 'index';
    const isDownloadsPage = pageName === 'downloads';

    const [deviceInfo, setDeviceInfo] = useState({
        isAndroid: false,
        isIpad: false,
        isCrOS: false,
        isiOS: false,
    });
    const [banner, setBanner] = useState<string | null>(null); // lite, android, promotion
    const [toggleNavPage, setToggleNavPage] = useState<any>(null);

    useLayoutEffect(() => {
        // 仅在客户端执行设备检测
        setDeviceInfo({
            isAndroid: window.gatsbyIsAndroid || false,
            isIpad: window.gatsbyIsIpad || false,
            isCrOS: window.gatsbyIsCrOS || false,
            isiOS: window.gatsbyIsIos || false,
        });
    }, []);
    const { isAndroid, isIpad, isCrOS, isiOS } = deviceInfo;
    useEffect(() => {
        if (getShowPromoBanner()) {
            return setBanner('promotion');
        }
        if (!isHomepage && !isDownloadsPage) {
            return setBanner('lite');
        } else {
            return setBanner(null);
        }
    }, [isIpad, isiOS, isCrOS, isAndroid, isHomepage, isDownloadsPage]);

    const getBanner = (type: string) => {
        if (type === 'lite') {
            return <LiteBanner {...({ navText, locale } as any)} />;
        } else if (type === 'android') {
            return <AndroidBanner locale={locale} text={androidBannerText} />;
        } else if (type === 'promotion') {
            return <PromoBanner />;
        }
    };

    let content = (
        <>
            <div className={'container-fluid px-0 mx-0'}>
                <CompatibilityPopup pageContext={{ text: compatibilityText, locale: locale, pageName: pageName }}>
                    <header className="global-header">
                        {banner !== null && getBanner(banner)}
                        <Navigation
                            pageContext={{
                                unlocalizedPath: unlocalizedPath,
                                navText: navText,
                            }}
                            toggleNavPage={toggleNavPage}
                        />
                    </header>

                    <main
                        className={`container-fluid shiroi px-0 ${banner !== null ? 'hasBanner' : ''}
                            ${banner === 'android' ? 'isAndroid' : ''}`}
                    >
                        <NavToggleContext.Provider value={(page) => setToggleNavPage(page)}>
                            {children}
                        </NavToggleContext.Provider>
                    </main>
                </CompatibilityPopup>

                <Footer pageContext={{ footerText: footerText }} />
            </div>
        </>
    );

    if (
        pageName === 'downloads_complete_classic' ||
        pageName === 'downloads_complete' ||
        pageName === 'downloads_complete_neo'
    ) {
        content = (
            <>
                <div className="container-fluid px-0 mx-0">
                    <CompatibilityPopup
                        pageContext={{
                            text: compatibilityText,
                            locale: locale,
                            pageName: pageName,
                        }}
                    >
                        {banner !== null && getBanner(banner)}
                        <header className="global-header">
                            <Navigation
                                pageContext={{
                                    unlocalizedPath: unlocalizedPath,
                                    navText: navText,
                                }}
                                toggleNavPage={toggleNavPage}
                            />
                        </header>

                        <main className={`container-fluid shiroi px-0 ${banner !== null ? 'hasBanner' : ''}`}>
                            <NavToggleContext.Provider value={(page) => setToggleNavPage(page)}>
                                {children}
                            </NavToggleContext.Provider>
                        </main>
                    </CompatibilityPopup>
                </div>
            </>
        );
    }

    // BTT brand landing page renders its own dark-themed nav/footer (per design),
    // so it opts out of the global Navigation/Footer/CompatibilityPopup/banner.
    if (pageName === 'btt-ecosystem') {
        content = <main className="btt-landing-root">{children}</main>;
    }
    if (
        pageName === 'btt_brand-voice' ||
        pageName === 'btt_buyback-burn' ||
        pageName === 'btt_buyback-burn_announcements'
    ) {
        content = (
            <main className="btt-next-root" style={{ marginTop: 0 }}>
                {children}
            </main>
        );
    }

    return (
        <LocaleContext.Provider value={{ locale }}>
            {content}
        </LocaleContext.Provider>
    );
};

export { Layout, LocaleContext };
